import React, { FC, ReactNode, useState } from 'react'
import { toastManager } from '@systemeio/components'
import { Pencil01Icon } from '@systemeio/icons'
import { cn } from 'cn'
import { useTranslation } from 'next-i18next/pages'
import { ImageCropperAndUploader } from 'shared/components/image-uploader/components/image-cropper-and-uploader'
import {
  IMAGE_UPLOAD_SIZE_LIMIT_BYTES,
  IMAGE_UPLOAD_SIZE_LIMIT_MB,
  imageMIMETypes,
} from 'shared/constants'
import { useDrop } from 'shared/hooks/use-drop'
import { DataFile } from 'shared/types/datafile-type'

interface AvatarUploaderProps {
  onSetPhoto: (file: DataFile) => void
  children: ReactNode
  source?: string
  width?: number
  height?: number
  type?: string
}

export const AvatarUploader: FC<AvatarUploaderProps> = ({
  onSetPhoto,
  children,
  width,
  height,
  source,
  type,
}): ReactNode => {
  const { t } = useTranslation()
  const [imageFile, setImageFile] = useState<File>()
  const [isOpened, setIsOpened] = useState(false)

  const { isDragging, handleDragOver, handleDragLeave, handleDrop, handleDragStart } = useDrop({
    onDrop: event => {
      const file = event.dataTransfer?.files?.[0]
      if (file) {
        onAddFile(file)
      }
    },
  })

  const onAddFile = (file: File): void => {
    if (file.size > IMAGE_UPLOAD_SIZE_LIMIT_BYTES) {
      toastManager.add({
        type: 'error',
        title: t('post.attachments.file_upload.size_limit_reached', {
          size: `${IMAGE_UPLOAD_SIZE_LIMIT_MB}MB`,
        }),
      })
      return
    }

    setImageFile(file)
    setIsOpened(true)
  }

  const onClose = (): void => {
    setImageFile(undefined)
  }

  const handleSelectFile = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0]
    if (file) {
      if (
        imageMIMETypes
          .split(',')
          .map(type => type.trim())
          .includes(file.type)
      ) {
        onAddFile(file)
      } else {
        toastManager.add({ type: 'error', title: t('file_selector.file_not_supported') })
      }
    }
    // NOTE: Reset value to have the ability to open the same file https://stackoverflow.com/questions/4109276/how-to-detect-input-type-file-change-for-the-same-file
    e.target.value = ''
  }

  return (
    <>
      <label>
        <input type="file" className="invisible absolute h-1 w-0" onChange={handleSelectFile} />
        <div
          className={cn(
            'relative cursor-pointer rounded-full',
            isDragging && 'ring-2 ring-border-accent',
          )}
          style={{
            width: width,
            height: height,
          }}
          onDragStart={handleDragStart}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          {children}
          <div className="absolute right-0 bottom-0 flex size-5 translate-x-1/3 items-center justify-center rounded-full bg-bg-accent-active p-1">
            <Pencil01Icon className="size-4 text-text-on" />
          </div>
        </div>
      </label>
      <ImageCropperAndUploader
        type={type}
        imageFile={imageFile}
        source={source}
        isOpened={isOpened}
        setIsOpened={setIsOpened}
        onClose={onClose}
        onUploadSuccess={onSetPhoto}
        isRounded={true}
      />
    </>
  )
}
